import React from 'react';
import { Field } from 'lib/components/modal';
import { Activity, ICombinedUserData } from 'lib/context';
import { EllipsisDiv, Recipient, TableCell } from './styles';
import { HoverElement } from 'app/pages/admin/index.styled';
import { EmailStatus } from 'lib/const/EmailReceivers';
import { VideoData } from 'lib/hooks';
import { CustomerHover } from 'app/pages/repairOrders/components';
import { copyField, getDisplayName } from 'lib/utils/functions';

import { parseDeliveryUnixTimestampToUserTimezone } from 'app/pages/account/userAccount/schedule/utils';
import { StatusBadge, Tooltip } from 'react-covideo-common';
import { BadgeThemeKey } from 'react-covideo-common/dist/components/StatusBadge/types';

export function getEmailStatusBadgeProps(
  emailStatusRaw: string,
  viewCount: number
): { variant: BadgeThemeKey; displayValue: string } {
  const status = emailStatusRaw?.toLowerCase();

  switch (status) {
    case EmailStatus.BOUNCE:
    case EmailStatus.SPAM:
    case EmailStatus.DROPPED:
    case EmailStatus.DEFFERED:
      return {
        variant: 'error',
        displayValue: status === EmailStatus.SPAM ? 'Spam' : emailStatusRaw,
      };

    case EmailStatus.OPEN:
      if (viewCount === 1) {
        return {
          variant: 'success',
          displayValue: EmailStatus.VIEWED,
        };
      }
      return {
        variant: 'success',
        displayValue: emailStatusRaw,
      };

    case EmailStatus.VIEWED:
      return {
        variant: 'success',
        displayValue: emailStatusRaw,
      };

    default:
      return {
        variant: 'neutral',
        displayValue: emailStatusRaw,
      };
  }
}

export enum ActivityTableColumns {
  DATE = 'dateSentUnix',
  RECIPIENT = 'recipientName',
  METHOD = 'method',
  VIDEO = 'videoTitle',
  STATUS = 'emailStatus',
  VIEWS = 'viewCount',
  SMS = 'phone',
  EMAIL = 'email',
  USER = 'user',
}

export enum ActivityFeedSortKey {
  DATE_SENT = 'DATE_SENT',
  USER_NAME = 'USERNAME',
  VIDEO_TITLE = 'VIDEO_TITLE',
}

export const baseFields = [
  {
    label: 'Date Sent',
    value: ActivityTableColumns.DATE,
    sortable: true,
    sortKey: ActivityFeedSortKey.DATE_SENT,
  },
  {
    label: `Recipient's Name`,
    value: ActivityTableColumns.RECIPIENT,
  },
  {
    label: 'Method',
    value: ActivityTableColumns.METHOD,
  },
  {
    label: 'Video Title',
    value: ActivityTableColumns.VIDEO,
    sortable: true,
    sortKey: ActivityFeedSortKey.VIDEO_TITLE,
  },
  {
    label: 'Status',
    value: ActivityTableColumns.STATUS,
  },
  {
    label: 'Views',
    value: ActivityTableColumns.VIEWS,
  },
];

export const filterTableFields = (fields: Field[]) =>
  fields.filter(item => !!item.label);

export const prepareCsvData = (
  activities: Activity[],
  userData: ICombinedUserData
) => {
  return activities.map(item => {
    return {
      [ActivityTableColumns.DATE]: ` ${parseDeliveryUnixTimestampToUserTimezone(
        item?.dateSentUnix || 0,
        userData
      )}`,
      ...(userData.isCompanyAdmin
        ? {
            [ActivityTableColumns.USER]: `${item.user?.firstName} ${item.user?.lastName}`,
          }
        : {}),
      [ActivityTableColumns.RECIPIENT]: getDisplayName([
        item.recipient.firstName,
        item.recipient.lastName,
      ]),
      [ActivityTableColumns.VIDEO]: item.videoTitle || item.video.title,
      [ActivityTableColumns.METHOD]: item.method,
      [ActivityTableColumns.STATUS]:
        item.emailStatus.charAt(0).toUpperCase() + item.emailStatus.slice(1),
      [ActivityTableColumns.VIEWS]: item.viewCount,
      [ActivityTableColumns.EMAIL]: item.recipient.email,
      [ActivityTableColumns.SMS]: item.recipient.phone,
    };
  });
};

export const formatColumns = (
  activity: Activity,
  tableFields: Field[],
  userData: ICombinedUserData,
  setShowVideoPreview: (video: VideoData) => void,
  allTableFields: Field[]
) => {
  return tableFields.map(item => {
    const cellValue = activity[item.value as keyof Activity];

    switch (item.value) {
      case ActivityTableColumns.DATE:
        const value = parseDeliveryUnixTimestampToUserTimezone(
          cellValue as number,
          userData
        );
        return <TableCell>{value}</TableCell>;
      case ActivityTableColumns.RECIPIENT:
        const hasContact =
          !!activity.recipient.phone || !!activity.recipient.email;

        const recipientName =
          getDisplayName([
            activity.recipient.firstName,
            activity.recipient.lastName,
          ]) ||
          activity.recipient.email ||
          activity.recipient.phone ||
          '-';

        return (
          <TableCell>
            {hasContact ? (
              <Tooltip
                popup={
                  <CustomerHover
                    phone={activity.recipient.phone}
                    email={activity.recipient.email}
                    maxWidth='220px'
                    onClick={(value: string, type: string) =>
                      copyField(value, type)
                    }
                  />
                }
                position='top'
              >
                <Recipient>{recipientName}</Recipient>
              </Tooltip>
            ) : (
              <Recipient>{recipientName}</Recipient>
            )}
          </TableCell>
        );
      case ActivityTableColumns.VIDEO:
        const title = activity.videoTitle || activity.video.title; // new activity will always have videoTitle, but older may not
        const videoExists = !!activity.video.id;

        const hidePopup =
          title.length < 20 || tableFields.length < allTableFields.length;
        return (
          <HoverElement style={{ width: '250px' }} top='-35px' center>
            {!hidePopup && <div className='hover-content'>{title}</div>}

            <TableCell
              style={{ ...(videoExists && { cursor: 'pointer' }) }}
              onClick={() => videoExists && setShowVideoPreview(activity.video)}
            >
              <EllipsisDiv width='250px'>{title || '-'}</EllipsisDiv>
            </TableCell>
          </HoverElement>
        );
      case ActivityTableColumns.METHOD:
        return (
          <TableCell justifyContent='center'>
            <StatusBadge
              size='small'
              text={activity.method}
              extendStyles={{ text: { textTransform: 'capitalize' } }}
              variant={'neutral'}
            />
          </TableCell>
        );
      case ActivityTableColumns.STATUS:
        const status = activity.emailStatus;
        const viewCount = activity.viewCount;

        const { variant, displayValue } = getEmailStatusBadgeProps(
          status,
          viewCount
        );

        return (
          <TableCell justifyContent='center'>
            {!!status ? (
              <StatusBadge
                size='small'
                variant={variant}
                text={displayValue}
                extendStyles={{ text: { textTransform: 'capitalize' } }}
              />
            ) : (
              '-'
            )}
          </TableCell>
        );
      case ActivityTableColumns.VIEWS:
        return (
          <TableCell justifyContent='right' width='50px'>
            {activity.viewCount}
          </TableCell>
        );
      case ActivityTableColumns.USER:
        return (
          <TableCell>
            <HoverElement top='-35px' center style={{ padding: 0 }}>
              <div className='hover-content'>
                {activity.user?.firstName} {activity.user?.lastName}
              </div>
              <Recipient>
                {activity.user?.firstName || '-'}{' '}
                {activity.user?.lastName || '-'}
              </Recipient>
            </HoverElement>
          </TableCell>
        );
      default:
        return <TableCell>{cellValue}</TableCell>;
    }
  });
};
